import * as React from 'react';
import {
    Checkbox,
    CommandBar,
    DefaultButton,
    DetailsList,
    DetailsListLayoutMode,
    Dialog,
    DialogFooter,
    DialogType,
    Dropdown,
    IDropdownOption,
    IColumn,
    ICommandBarItemProps,
    IconButton,
    Label,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Selection,
    SelectionMode,
    Separator,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
} from '@fluentui/react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiHelper } from '../../../ApiHelper';
import { useConfig } from '../../../configContext';
import { Service } from '../../../interfaces/Service';
import {
    AGENCY_SPEC_FIELD_DESCRIPTORS,
    AgencyModelPickerItem,
    AgencyModelsSettingResult,
    ARTIFACT_TYPE_OPTIONS,
    AgentArtifactEditorState,
    AgencySpecReferenceFormat,
    JobDefinitionCollectionResult,
    JobDefinitionDetailResult,
    JobDefinitionEditorState,
    JobDefinitionSummary,
    ExtractionArtifactEditorState,
    PipelineArtifactEditorState,
    PluginArtifactEditorState,
    ArtifactCollectionResult,
    ArtifactEditorState,
    ArtifactPreviewResult,
    ArtifactSummary,
    DEFINITION_SECTION_OPTIONS,
    DefinitionSection,
    JOB_RUNTIME_SUBSTITUTION_OPTIONS,
    PageShell,
    TASK_TYPES,
    buildJobDefinitionUrl,
    containsSnippetSyntax,
    getSnippetArtifactValidationError,
    getArtifactTypeLabel,
    getTaskTypeLabel,
    insertSnippetReference,
    insertSubstitutionReference,
    normalizeJobModelConfiguration,
    parseAgencyModelsSettingResponse,
    updateSearchParams,
    useCurrentService,
    useDocumentTitle,
} from '../shared';
import { DefinitionCatalog } from './definitions/DefinitionCatalog';
import { DefinitionEditor } from './definitions/DefinitionEditor';
import { DuplicateDefinitionPanel } from './definitions/DuplicateDefinitionPanel';
import {
    AgencyAgentReference,
    AgencyPluginReference,
    agencyReferencesEqual,
    buildBundledAgentReference,
    buildStoredBundledAgentReference,
    buildSpecEncodedReference,
    derivePluginFromBundledAgent,
    normalizeSpecSegments,
    parseAgencyPluginArtifact,
    parseSpecEncodedReference,
} from '../agencyArtifactReferences';
import type {
    AgencyArtifactResolutionType,
    JobResultExtractionDestination,
    JobResultExtractionMode,
    JobResultExtractionSource,
} from '../types';

function createEmptyJobForm(): JobDefinitionEditorState {
    return {
        name: '',
        taskType: 0,
        text: '',
        disabled: false,
        canCreatePR: false,
        canPublishPR: false,
        agentArtifactId: undefined,
        pipelineArtifactId: undefined,
        dependsOnJobDefinitionId: undefined,
        executionBackend: 0,
        maxConcurrency: '',
        effectiveMaxConcurrency: 5,
        modelConfiguration: null,
        artifactIds: [],
        extractionArtifactIds: [],
        pluginArtifactIds: [],
    };
}

function getDefaultMaxConcurrencyForBackend(executionBackend: number) {
    return executionBackend === 1 ? 100 : 5;
}

function createEmptyAgentForm(): AgentArtifactEditorState {
    return {
        name: '',
        disabled: false,
        runtimeName: '',
        resolutionType: 'Company',
        pluginName: '',
        selectedPluginArtifactId: undefined,
        unmatchedPluginReference: undefined,
        unmatchedPluginResolutionType: undefined,
        inputs: [],
    };
}

function createEmptyPluginForm(): PluginArtifactEditorState {
    return {
        name: '',
        disabled: false,
        runtimeName: '',
        resolutionType: 'Company',
        specFormat: 'market',
        specSegments: createEmptySpecSegments('market'),
    };
}

function createEmptyPipelineForm(): PipelineArtifactEditorState {
    return {
        name: '',
        disabled: false,
        pipelineId: '',
        projectId: '',
        pipelineRefOverride: '',
    };
}

function createEmptyExtractionForm(): ExtractionArtifactEditorState {
    return {
        name: '',
        disabled: false,
        purpose: 'GenericOutput',
        source: 'LastAssistantText',
        mode: 'Regex',
        regexPattern: '',
        ignoreCase: false,
        multiline: false,
        singleline: false,
        startDelimiter: '',
        endDelimiter: '',
        destination: 'Output1',
    };
}

function createEmptySpecSegments(format: AgencySpecReferenceFormat): string[] {
    return AGENCY_SPEC_FIELD_DESCRIPTORS[format].map(() => '');
}

function validatePluginSpecForm(form: PluginArtifactEditorState): string | null {
    const descriptors = AGENCY_SPEC_FIELD_DESCRIPTORS[form.specFormat];
    const segments = normalizeSpecSegments(form.specFormat, form.specSegments);
    const missingField = descriptors.find((descriptor, index) => !descriptor.optional && segments[index].trim().length === 0);
    if (!missingField) {
        return null;
    }

    return `${missingField.label} is required for ${form.specFormat} spec references.`;
}

function buildSpecEncodedReferencePreview(format: AgencySpecReferenceFormat, segments: string[]): string {
    return buildSpecEncodedReference(format, segments);
}

function getSpecWarning(format: AgencySpecReferenceFormat): string | null {
    if (format === 'market') {
        return null;
    }

    return 'Prefer market: when possible. ado-git: and github: are still a work in progress.';
}

function parseArtifactJson<T>(artifact: ArtifactSummary, fallback: T): T {
    if (!artifact.text?.trim()) {
        return fallback;
    }

    try {
        return {
            ...fallback,
            ...(JSON.parse(artifact.text) as Partial<T>),
        };
    } catch {
        return fallback;
    }
}

function getPluginReference(artifact: ArtifactSummary): AgencyPluginReference {
    return parseAgencyPluginArtifact(artifact.name, artifact.text);
}

function mapAgentArtifactToForm(
    artifact: ArtifactSummary,
    pluginArtifacts: ArtifactSummary[],
): AgentArtifactEditorState {
    const parsed = parseArtifactJson(artifact, {
        name: '',
        type: 'Company' as AgencyArtifactResolutionType,
        pluginName: '',
        pluginArtifactName: '',
        inputs: {} as Record<string, string>,
    });

    const referencedPlugin = parsed.pluginArtifactName?.trim()
        ? pluginArtifacts.find(candidate => candidate.name.localeCompare(parsed.pluginArtifactName, undefined, { sensitivity: 'accent' }) === 0)
        : undefined;
    const agentReference: AgencyAgentReference = {
        name: parsed.name ?? '',
        type: parsed.type ?? 'Company',
        pluginName: parsed.pluginName ?? undefined,
    };
    const bundledAgent = referencedPlugin ? null : derivePluginFromBundledAgent(agentReference);
    const selectedPlugin = referencedPlugin ?? (bundledAgent
        ? pluginArtifacts.find(candidate => agencyReferencesEqual(getPluginReference(candidate), bundledAgent.plugin))
        : undefined);

    return {
        name: artifact.name,
        disabled: artifact.disabled,
        runtimeName: bundledAgent ? bundledAgent.agentName : parsed.name ?? '',
        resolutionType: selectedPlugin ? getPluginReference(selectedPlugin).type : parsed.type ?? 'Company',
        pluginName: parsed.pluginName ?? '',
        selectedPluginArtifactId: selectedPlugin?.id,
        unmatchedPluginReference: parsed.pluginArtifactName?.trim() && !referencedPlugin
            ? parsed.pluginArtifactName.trim()
            : bundledAgent && !selectedPlugin ? bundledAgent.plugin.name : undefined,
        unmatchedPluginResolutionType: bundledAgent && !selectedPlugin ? bundledAgent.plugin.type : undefined,
        inputs: Object.entries(parsed.inputs ?? {}).map(([key, value]) => ({ key, value: value ?? '' })),
    };
}

function mapPluginArtifactToForm(artifact: ArtifactSummary): PluginArtifactEditorState {
    const parsed = parseArtifactJson(artifact, {
        name: '',
        type: 'Company' as AgencyArtifactResolutionType,
    });
    const parsedSpec = parseSpecEncodedReference(parsed.name);

    return {
        name: artifact.name,
        disabled: artifact.disabled,
        runtimeName: parsed.name?.trim() || artifact.name,
        resolutionType: parsed.type ?? 'Company',
        specFormat: parsedSpec?.format ?? 'market',
        specSegments: parsedSpec?.segments?.length ? normalizeSpecSegments(parsedSpec.format, parsedSpec.segments) : createEmptySpecSegments('market'),
    };
}

function mapPipelineArtifactToForm(artifact: ArtifactSummary): PipelineArtifactEditorState {
    const parsed = parseArtifactJson(artifact, {
        pipelineId: undefined as number | string | undefined,
        projectId: '',
        pipelineRefOverride: '',
    });

    return {
        name: artifact.name,
        disabled: artifact.disabled,
        pipelineId: typeof parsed.pipelineId === 'number'
            ? String(parsed.pipelineId)
            : typeof parsed.pipelineId === 'string'
                ? parsed.pipelineId
                : '',
        projectId: typeof parsed.projectId === 'string' ? parsed.projectId : '',
        pipelineRefOverride: typeof parsed.pipelineRefOverride === 'string' ? parsed.pipelineRefOverride : '',
    };
}

export function mapExtractionArtifactToForm(artifact: ArtifactSummary): ExtractionArtifactEditorState {
    const parsed = parseArtifactJson(artifact, {
        purpose: 'GenericOutput' as ExtractionArtifactEditorState['purpose'],
        source: 'LastAssistantText' as JobResultExtractionSource,
        mode: 'Regex' as JobResultExtractionMode,
        regexPattern: '',
        ignoreCase: false,
        multiline: false,
        singleline: false,
        startDelimiter: '',
        endDelimiter: '',
        destination: 'Output1' as JobResultExtractionDestination,
    });

    return {
        name: artifact.name,
        disabled: artifact.disabled,
        purpose: parsed.purpose ?? 'GenericOutput',
        source: parsed.source ?? 'LastAssistantText',
        mode: parsed.mode ?? 'Regex',
        regexPattern: typeof parsed.regexPattern === 'string' ? parsed.regexPattern : '',
        ignoreCase: !!parsed.ignoreCase,
        multiline: !!parsed.multiline,
        singleline: !!parsed.singleline,
        startDelimiter: typeof parsed.startDelimiter === 'string' ? parsed.startDelimiter : '',
        endDelimiter: typeof parsed.endDelimiter === 'string' ? parsed.endDelimiter : '',
        destination: parsed.destination ?? 'Output1',
    };
}

function serializeAgentArtifactForm(
    form: AgentArtifactEditorState,
    pluginArtifacts: ArtifactSummary[],
): string {
    const populatedInputs = form.inputs.filter(input => input.key.trim().length > 0 || input.value.trim().length > 0);
    const inputs = populatedInputs.length > 0
        ? Object.fromEntries(populatedInputs.map(input => [input.key.trim(), input.value]))
        : undefined;

    const selectedPlugin = pluginArtifacts.find(plugin => plugin.id === form.selectedPluginArtifactId);
    const agentReference = selectedPlugin
        ? buildStoredBundledAgentReference(
            form.runtimeName,
            selectedPlugin.name,
            getPluginReference(selectedPlugin),
        )
        : form.unmatchedPluginReference && form.unmatchedPluginResolutionType
            ? buildBundledAgentReference(form.runtimeName, {
                name: form.unmatchedPluginReference,
                type: form.unmatchedPluginResolutionType,
            })
        : {
            name: form.runtimeName.trim(),
            type: form.resolutionType,
            ...(form.pluginName.trim() ? { pluginName: form.pluginName.trim() } : {}),
        };

    return JSON.stringify({
        ...agentReference,
        ...(inputs ? { inputs } : {}),
    });
}

function serializePluginArtifactForm(form: PluginArtifactEditorState): string {
    return JSON.stringify({
        name: form.resolutionType === 'Spec'
            ? buildSpecEncodedReference(form.specFormat, form.specSegments)
            : form.runtimeName.trim(),
        type: form.resolutionType,
    });
}

function serializePipelineArtifactForm(form: PipelineArtifactEditorState): string {
    const pipelineId = Number(form.pipelineId);
    return JSON.stringify({
        $type: 'pipeline',
        pipelineId,
        ...(form.projectId.trim() ? { projectId: form.projectId.trim() } : {}),
        ...(form.pipelineRefOverride.trim() ? { pipelineRefOverride: form.pipelineRefOverride.trim() } : {}),
    });
}

export function serializeExtractionArtifactForm(form: ExtractionArtifactEditorState): string {
    return JSON.stringify({
        purpose: form.purpose,
        source: form.source,
        mode: form.mode,
        ...(form.mode === 'Regex'
            ? {
                regexPattern: form.regexPattern,
                ignoreCase: !!form.ignoreCase,
                multiline: !!form.multiline,
                singleline: !!form.singleline,
            }
            : {
                startDelimiter: form.startDelimiter,
                ...(form.endDelimiter.trim() ? { endDelimiter: form.endDelimiter } : {}),
            }),
        destination: form.destination,
    });
}

export function JobDefinitionsPage() {
    const { serviceName } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const service = useCurrentService(serviceName);
    const config = useConfig();

    const allServices = React.useMemo(
        () => ((config?.services ?? []) as Service[]).slice().sort((left, right) => left.name.localeCompare(right.name)),
        [config],
    );

    const section = React.useMemo<DefinitionSection>(() => {
        const rawSection = searchParams.get('section');
        if (rawSection === 'agents' || rawSection === 'plugins' || rawSection === 'pipelines' || rawSection === 'extractions' || rawSection === 'skills-scripts' || rawSection === 'snippets') {
            return rawSection;
        }

        return 'jobs';
    }, [searchParams]);
    const mode = searchParams.get('mode') === 'create' ? 'create' : 'edit';
    const selectedName = searchParams.get('name') ?? '';
    useDocumentTitle(selectedName ? `${selectedName} - Job definitions` : 'Job definitions');

    const [definitions, setDefinitions] = React.useState<JobDefinitionSummary[]>([]);
    const [artifacts, setArtifacts] = React.useState<ArtifactSummary[]>([]);
    const [catalogLoading, setCatalogLoading] = React.useState(false);
    const [catalogError, setCatalogError] = React.useState<string | null>(null);
    const [detailLoading, setDetailLoading] = React.useState(false);
    const [detailError, setDetailError] = React.useState<string | null>(null);
    const [detailReloadToken, setDetailReloadToken] = React.useState(0);
    const [jobForm, setJobForm] = React.useState<JobDefinitionEditorState>(createEmptyJobForm);
    const [agentForm, setAgentForm] = React.useState<AgentArtifactEditorState>(createEmptyAgentForm);
    const [pluginForm, setPluginForm] = React.useState<PluginArtifactEditorState>(createEmptyPluginForm);
    const [pipelineForm, setPipelineForm] = React.useState<PipelineArtifactEditorState>(createEmptyPipelineForm);
    const [extractionForm, setExtractionForm] = React.useState<ExtractionArtifactEditorState>(createEmptyExtractionForm);
    const [artifactForm, setArtifactForm] = React.useState<ArtifactEditorState>({
        name: '',
        type: 1,
        text: '',
        disabled: false,
    });
    const [saving, setSaving] = React.useState(false);
    const [saveError, setSaveError] = React.useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = React.useState<string | null>(null);
    const [previewLoading, setPreviewLoading] = React.useState(false);
    const [previewError, setPreviewError] = React.useState<string | null>(null);
    const [previewText, setPreviewText] = React.useState<string | null>(null);
    const [agencyModelOptions, setAgencyModelOptions] = React.useState<AgencyModelPickerItem[]>([]);
    const [agencyModelsLoading, setAgencyModelsLoading] = React.useState(false);
    const [agencyModelsMessage, setAgencyModelsMessage] = React.useState<string | null>(null);
    const [hasSnippetPreview, setHasSnippetPreview] = React.useState(false);
    const [selectedSnippetName, setSelectedSnippetName] = React.useState<string | undefined>(undefined);
    const [selectedSubstitutionToken, setSelectedSubstitutionToken] = React.useState<string | undefined>(JOB_RUNTIME_SUBSTITUTION_OPTIONS[0]?.token);
    const [selectedInsertionKind, setSelectedInsertionKind] = React.useState<'snippet' | 'substitution'>('substitution');
    const [relationshipToAddId, setRelationshipToAddId] = React.useState<number | undefined>(undefined);
    const [extractionRelationshipToAddId, setExtractionRelationshipToAddId] = React.useState<number | undefined>(undefined);
    const [pluginRelationshipToAddId, setPluginRelationshipToAddId] = React.useState<number | undefined>(undefined);
    const [isDuplicatePanelOpen, setDuplicatePanelOpen] = React.useState(false);
    const [duplicateTargetServiceId, setDuplicateTargetServiceId] = React.useState<number | undefined>(service?.id);
    const [duplicateTargetName, setDuplicateTargetName] = React.useState('');
    const [duplicateTaskType, setDuplicateTaskType] = React.useState<number>(0);
    const [duplicateSubmitting, setDuplicateSubmitting] = React.useState(false);
    const [duplicateError, setDuplicateError] = React.useState<string | null>(null);
    const editorRef = React.useRef<HTMLTextAreaElement | null>(null);

    const updateRoute = React.useCallback((updates: Record<string, string | number | null | undefined>) => {
        updateSearchParams(searchParams, setSearchParams, updates);
    }, [searchParams, setSearchParams]);

    const loadCatalog = React.useCallback(async () => {
        if (!serviceName) {
            setDefinitions([]);
            setArtifacts([]);
            return;
        }

        setCatalogLoading(true);
        setCatalogError(null);

        try {
            const [definitionResponse, artifactResponse, pluginResponse] = await Promise.all([
                ApiHelper(`/api/service/${serviceName}/jobdefinitions?includeDisabled=true`) as Promise<JobDefinitionCollectionResult>,
                ApiHelper(`/api/service/${serviceName}/artifacts?includeDisabled=true`) as Promise<ArtifactCollectionResult>,
                ApiHelper(`/api/service/${serviceName}/artifacts?type=5&includeDisabled=true&includeText=true`) as Promise<ArtifactCollectionResult>,
            ]);

            const nextDefinitions = (definitionResponse.jobDefinitions ?? []).slice().sort((left, right) => left.name.localeCompare(right.name));
            const pluginArtifactsById = new Map((pluginResponse.artifacts ?? []).map(artifact => [artifact.id, artifact]));
            const nextArtifacts = (artifactResponse.artifacts ?? [])
                .map(artifact => pluginArtifactsById.get(artifact.id) ?? artifact)
                .sort((left, right) => left.name.localeCompare(right.name));

            setDefinitions(nextDefinitions);
            setArtifacts(nextArtifacts);
        } catch (err) {
            setCatalogError(err instanceof Error ? err.message : 'Failed to load job definitions.');
            setDefinitions([]);
            setArtifacts([]);
        } finally {
            setCatalogLoading(false);
        }
    }, [serviceName]);

    React.useEffect(() => {
        void loadCatalog();
    }, [loadCatalog]);

    React.useEffect(() => {
        if (!serviceName) {
            setAgencyModelOptions([]);
            setAgencyModelsLoading(false);
            setAgencyModelsMessage(null);
            return;
        }

        let isMounted = true;
        setAgencyModelsLoading(true);
        setAgencyModelsMessage(null);

        ApiHelper(`/api/service/${serviceName}/agencymodels`)
            .then((response: AgencyModelsSettingResult) => {
                if (!isMounted) {
                    return;
                }

                try {
                    const parsedModels = parseAgencyModelsSettingResponse(response);
                    setAgencyModelOptions(parsedModels);
                    if (!response.found || !response.value?.trim()) {
                        setAgencyModelsMessage('No Agency models are configured. Add the AgencyModels setting to enable model selection.');
                    } else if (parsedModels.length === 0) {
                        setAgencyModelsMessage('AgencyModels is configured, but it does not contain any models with modelPickerEnabled set to true.');
                    } else {
                        setAgencyModelsMessage(null);
                    }
                } catch (err) {
                    setAgencyModelOptions([]);
                    setAgencyModelsMessage(err instanceof Error ? `Failed to parse AgencyModels: ${err.message}` : 'Failed to parse AgencyModels.');
                } finally {
                    setAgencyModelsLoading(false);
                }
            })
            .catch((err: Error) => {
                if (!isMounted) {
                    return;
                }

                setAgencyModelOptions([]);
                setAgencyModelsMessage(err.message || 'Failed to load Agency models.');
                setAgencyModelsLoading(false);
            });

        return () => {
            isMounted = false;
        };
    }, [serviceName]);

    const agentArtifacts = React.useMemo(
        () => artifacts.filter(artifact => artifact.type === 4),
        [artifacts],
    );
    const pluginDefinitionArtifacts = React.useMemo(
        () => artifacts.filter(artifact => artifact.type === 5),
        [artifacts],
    );
    const pipelineArtifacts = React.useMemo(
        () => artifacts.filter(artifact => artifact.type === 6),
        [artifacts],
    );
    const extractionArtifacts = React.useMemo(
        () => artifacts.filter(artifact => artifact.type === 7),
        [artifacts],
    );
    const skillScriptArtifacts = React.useMemo(
        () => artifacts.filter(artifact => artifact.type === 1 || artifact.type === 2),
        [artifacts],
    );
    const snippetArtifacts = React.useMemo(
        () => artifacts.filter(artifact => artifact.type === 3),
        [artifacts],
    );
    const enabledSnippets = React.useMemo(
        () => snippetArtifacts.filter(artifact => !artifact.disabled).slice().sort((left, right) => left.name.localeCompare(right.name)),
        [snippetArtifacts],
    );
    const currentSectionItems = React.useMemo(() => {
        switch (section) {
            case 'jobs':
                return definitions.map(definition => ({ name: definition.name, disabled: definition.disabled, subtitle: getTaskTypeLabel(definition.taskType) }));
            case 'agents':
                return agentArtifacts.map(artifact => ({ name: artifact.name, disabled: artifact.disabled, subtitle: getArtifactTypeLabel(artifact.type) }));
            case 'plugins':
                return pluginDefinitionArtifacts.map(artifact => ({ name: artifact.name, disabled: artifact.disabled, subtitle: getArtifactTypeLabel(artifact.type) }));
            case 'pipelines':
                return pipelineArtifacts.map(artifact => ({ name: artifact.name, disabled: artifact.disabled, subtitle: getArtifactTypeLabel(artifact.type) }));
            case 'extractions':
                return extractionArtifacts.map(artifact => ({ name: artifact.name, disabled: artifact.disabled, subtitle: getArtifactTypeLabel(artifact.type) }));
            case 'skills-scripts':
                return skillScriptArtifacts.map(artifact => ({ name: artifact.name, disabled: artifact.disabled, subtitle: getArtifactTypeLabel(artifact.type) }));
            case 'snippets':
                return snippetArtifacts.map(artifact => ({ name: artifact.name, disabled: artifact.disabled, subtitle: getArtifactTypeLabel(artifact.type) }));
            default:
                return [];
        }
    }, [agentArtifacts, definitions, extractionArtifacts, pipelineArtifacts, pluginDefinitionArtifacts, section, skillScriptArtifacts, snippetArtifacts]);

    React.useEffect(() => {
        if (catalogLoading) {
            return;
        }

        if (currentSectionItems.length === 0) {
            if (mode !== 'create' || selectedName) {
                updateRoute({ name: null, mode: 'create' });
            }
            return;
        }

        if (mode === 'create') {
            return;
        }

        const hasSelectedItem = selectedName && currentSectionItems.some(item => item.name.localeCompare(selectedName) === 0);
        if (!hasSelectedItem) {
            updateRoute({ name: currentSectionItems[0].name, mode: 'edit' });
        }
    }, [catalogLoading, currentSectionItems, mode, selectedName, updateRoute]);

    React.useEffect(() => {
        if (mode !== 'create') {
            return;
        }

        setDetailError(null);
        setSaveError(null);
        setSaveSuccess(null);
        setPreviewError(null);
        setPreviewText(null);
        setRelationshipToAddId(undefined);
        setExtractionRelationshipToAddId(undefined);
        setPluginRelationshipToAddId(undefined);

        if (section === 'jobs') {
            setJobForm(createEmptyJobForm());
        } else if (section === 'agents') {
            setAgentForm(createEmptyAgentForm());
        } else if (section === 'plugins') {
            setPluginForm(createEmptyPluginForm());
        } else if (section === 'pipelines') {
            setPipelineForm(createEmptyPipelineForm());
        } else if (section === 'extractions') {
            setExtractionForm(createEmptyExtractionForm());
        } else {
            setArtifactForm({
                name: '',
                type: section === 'snippets' ? 3 : 1,
                text: '',
                disabled: false,
            });
        }
    }, [mode, section]);

    React.useEffect(() => {
        if (!serviceName || mode === 'create' || !selectedName) {
            return;
        }

        let isMounted = true;
        setDetailLoading(true);
        setDetailError(null);
        setSaveError(null);
        setSaveSuccess(null);
        setPreviewError(null);
        setPreviewText(null);

        const endpoint = section === 'jobs'
            ? `/api/service/${serviceName}/jobdefinitions/${encodeURIComponent(selectedName)}`
            : `/api/service/${serviceName}/artifacts/${encodeURIComponent(selectedName)}`;

        ApiHelper(endpoint)
            .then((response: JobDefinitionDetailResult | ArtifactSummary) => {
                if (!isMounted) {
                    return;
                }

                if (section === 'jobs') {
                    const detail = response as JobDefinitionDetailResult;
                    setJobForm({
                        name: detail.name,
                        taskType: detail.taskType,
                        text: detail.text ?? '',
                        disabled: detail.disabled,
                        canCreatePR: detail.canCreatePR ?? false,
                        canPublishPR: detail.canPublishPR ?? false,
                        agentArtifactId: detail.agentArtifactId ?? undefined,
                        pipelineArtifactId: detail.pipelineArtifactId ?? undefined,
                        dependsOnJobDefinitionId: detail.dependsOnJobDefinitionId ?? undefined,
                        executionBackend: detail.executionBackend ?? 0,
                        maxConcurrency: detail.maxConcurrency !== null && detail.maxConcurrency !== undefined
                            ? String(detail.maxConcurrency)
                            : '',
                        effectiveMaxConcurrency: detail.effectiveMaxConcurrency ?? getDefaultMaxConcurrencyForBackend(detail.executionBackend ?? 0),
                        modelConfiguration: normalizeJobModelConfiguration(detail.modelConfiguration),
                        artifactIds: (detail.relatedArtifacts ?? [])
                            .filter(artifact => artifact.type === 1 || artifact.type === 2)
                            .map(artifact => artifact.id),
                        extractionArtifactIds: (detail.relatedArtifacts ?? [])
                            .filter(artifact => artifact.type === 7)
                            .map(artifact => artifact.id),
                        pluginArtifactIds: (detail.relatedArtifacts ?? [])
                            .filter(artifact => artifact.type === 5)
                            .map(artifact => artifact.id),
                    });
                } else if (section === 'agents') {
                    const detail = response as ArtifactSummary;
                    setAgentForm(mapAgentArtifactToForm(detail, pluginDefinitionArtifacts));
                } else if (section === 'plugins') {
                    const detail = response as ArtifactSummary;
                    setPluginForm(mapPluginArtifactToForm(detail));
                } else if (section === 'pipelines') {
                    const detail = response as ArtifactSummary;
                    setPipelineForm(mapPipelineArtifactToForm(detail));
                } else if (section === 'extractions') {
                    const detail = response as ArtifactSummary;
                    setExtractionForm(mapExtractionArtifactToForm(detail));
                } else {
                    const detail = response as ArtifactSummary;
                    setArtifactForm({
                        name: detail.name,
                        type: detail.type,
                        text: detail.text ?? '',
                        disabled: detail.disabled,
                    });
                }

                setDetailLoading(false);
            })
            .catch((err: Error) => {
                if (!isMounted) {
                    return;
                }

                setDetailError(err.message || 'Failed to load the selected definition.');
                setDetailLoading(false);
            });

        return () => {
            isMounted = false;
        };
    }, [detailReloadToken, mode, pluginDefinitionArtifacts, section, selectedName, serviceName]);

    const previewSourceText = section === 'jobs'
        ? jobForm.text
        : section === 'agents' || section === 'plugins' || section === 'pipelines' || section === 'extractions'
            ? ''
            : artifactForm.text;
    React.useEffect(() => {
        if (section === 'snippets' || section === 'agents' || section === 'plugins' || section === 'pipelines' || section === 'extractions') {
            setHasSnippetPreview(false);
            return;
        }

        const timeoutId = window.setTimeout(() => {
            setHasSnippetPreview(containsSnippetSyntax(previewSourceText));
        }, 400);

        return () => window.clearTimeout(timeoutId);
    }, [previewSourceText, section]);

    React.useEffect(() => {
        if (enabledSnippets.length === 0) {
            setSelectedSnippetName(undefined);
            return;
        }

        if (!selectedSnippetName || !enabledSnippets.some(snippet => snippet.name === selectedSnippetName)) {
            setSelectedSnippetName(enabledSnippets[0].name);
        }
    }, [enabledSnippets, selectedSnippetName]);
    React.useEffect(() => {
        if (JOB_RUNTIME_SUBSTITUTION_OPTIONS.length === 0) {
            setSelectedSubstitutionToken(undefined);
            return;
        }

        if (!selectedSubstitutionToken || !JOB_RUNTIME_SUBSTITUTION_OPTIONS.some(option => option.token === selectedSubstitutionToken)) {
            setSelectedSubstitutionToken(JOB_RUNTIME_SUBSTITUTION_OPTIONS[0].token);
        }
    }, [selectedSubstitutionToken]);

    const relationshipOptions = React.useMemo(
        () => skillScriptArtifacts
            .filter(artifact => !artifact.disabled && !jobForm.artifactIds.includes(artifact.id))
            .map(artifact => ({ key: artifact.id, text: `${artifact.name} (${getArtifactTypeLabel(artifact.type)})` })),
        [jobForm.artifactIds, skillScriptArtifacts],
    );
    const pluginRelationshipOptions = React.useMemo(
        () => pluginDefinitionArtifacts
            .filter(artifact => !artifact.disabled && !jobForm.pluginArtifactIds.includes(artifact.id))
            .map(artifact => ({ key: artifact.id, text: artifact.name })),
        [jobForm.pluginArtifactIds, pluginDefinitionArtifacts],
    );
    const extractionRelationshipOptions = React.useMemo(
        () => extractionArtifacts
            .filter(artifact => !artifact.disabled && !jobForm.extractionArtifactIds.includes(artifact.id))
            .map(artifact => ({ key: artifact.id, text: artifact.name })),
        [extractionArtifacts, jobForm.extractionArtifactIds],
    );
    const selectedAgentOptions = React.useMemo(
        () => [
            { key: 0, text: 'None' },
            ...agentArtifacts.map(artifact => ({
                key: artifact.id,
                text: artifact.disabled ? `${artifact.name} (disabled)` : artifact.name,
            })),
        ],
        [agentArtifacts],
    );
    const bundledPluginOptions = React.useMemo(
        () => [
            { key: 0, text: 'None' },
            ...pluginDefinitionArtifacts
                .filter(artifact => !artifact.disabled || artifact.id === agentForm.selectedPluginArtifactId)
                .map(artifact => {
                    const pluginReference = getPluginReference(artifact);
                    const label = `${artifact.name} (${pluginReference.type})`;
                    return {
                        key: artifact.id,
                        text: artifact.disabled ? `${label} (disabled)` : label,
                        data: {
                            resolutionType: pluginReference.type,
                        },
                    };
                }),
        ],
        [agentForm.selectedPluginArtifactId, pluginDefinitionArtifacts],
    );
    const selectedPipelineOptions = React.useMemo(
        () => [
            { key: 0, text: 'Default' },
            ...pipelineArtifacts.map(artifact => ({
                key: artifact.id,
                text: artifact.disabled ? `${artifact.name} (disabled)` : artifact.name,
            })),
        ],
        [pipelineArtifacts],
    );
    const dependsOnOptions = React.useMemo(() => {
        const currentJobName = mode === 'create' ? jobForm.name : selectedName;
        return [
            { key: 0, text: 'None' },
            ...definitions
                .filter(definition => !definition.canCreatePR && definition.name.localeCompare(currentJobName) !== 0)
                .map(definition => ({
                    key: definition.jobDefinitionId,
                    text: definition.disabled ? `${definition.name} (disabled)` : definition.name,
                }))
                .sort((left, right) => left.text.localeCompare(right.text)),
        ];
    }, [definitions, jobForm.name, mode, selectedName]);

    React.useEffect(() => {
        if (relationshipOptions.length === 0) {
            setRelationshipToAddId(undefined);
            return;
        }

        if (!relationshipToAddId || !relationshipOptions.some(option => Number(option.key) === relationshipToAddId)) {
            setRelationshipToAddId(Number(relationshipOptions[0].key));
        }
    }, [relationshipOptions, relationshipToAddId]);
    React.useEffect(() => {
        if (extractionRelationshipOptions.length === 0) {
            setExtractionRelationshipToAddId(undefined);
            return;
        }

        if (!extractionRelationshipToAddId || !extractionRelationshipOptions.some(option => Number(option.key) === extractionRelationshipToAddId)) {
            setExtractionRelationshipToAddId(Number(extractionRelationshipOptions[0].key));
        }
    }, [extractionRelationshipOptions, extractionRelationshipToAddId]);
    React.useEffect(() => {
        if (pluginRelationshipOptions.length === 0) {
            setPluginRelationshipToAddId(undefined);
            return;
        }

        if (!pluginRelationshipToAddId || !pluginRelationshipOptions.some(option => Number(option.key) === pluginRelationshipToAddId)) {
            setPluginRelationshipToAddId(Number(pluginRelationshipOptions[0].key));
        }
    }, [pluginRelationshipOptions, pluginRelationshipToAddId]);

    const artifactsById = React.useMemo(
        () => new Map(artifacts.map(artifact => [artifact.id, artifact])),
        [artifacts],
    );
    const relatedArtifacts = React.useMemo(
        () => jobForm.artifactIds
            .map(artifactId => artifactsById.get(artifactId))
            .filter((artifact): artifact is ArtifactSummary => !!artifact)
            .sort((left, right) => left.name.localeCompare(right.name)),
        [jobForm.artifactIds, artifactsById],
    );
    const pluginArtifacts = React.useMemo(
        () => jobForm.pluginArtifactIds
            .map(artifactId => artifactsById.get(artifactId))
            .filter((artifact): artifact is ArtifactSummary => !!artifact)
            .sort((left, right) => left.name.localeCompare(right.name)),
        [jobForm.pluginArtifactIds, artifactsById],
    );
    const selectedExtractionArtifacts = React.useMemo(
        () => jobForm.extractionArtifactIds
            .map(artifactId => artifactsById.get(artifactId))
            .filter((artifact): artifact is ArtifactSummary => !!artifact)
            .sort((left, right) => left.name.localeCompare(right.name)),
        [jobForm.extractionArtifactIds, artifactsById],
    );

    function openDuplicatePanel() {
        setDuplicateTargetServiceId(service?.id);
        setDuplicateTargetName(selectedName ? `${selectedName}-copy` : '');
        setDuplicateTaskType(jobForm.taskType);
        setDuplicateError(null);
        setDuplicatePanelOpen(true);
    }

    function handleAddRelationship() {
        if (!relationshipToAddId) {
            return;
        }

        setJobForm(current => ({
            ...current,
            artifactIds: current.artifactIds.includes(relationshipToAddId)
                ? current.artifactIds
                : [...current.artifactIds, relationshipToAddId],
        }));
        setSaveSuccess(null);
    }

    function handleAddPluginRelationship() {
        if (!pluginRelationshipToAddId) {
            return;
        }

        setJobForm(current => ({
            ...current,
            pluginArtifactIds: current.pluginArtifactIds.includes(pluginRelationshipToAddId)
                ? current.pluginArtifactIds
                : [...current.pluginArtifactIds, pluginRelationshipToAddId],
        }));
        setSaveSuccess(null);
    }

    function handleAddExtractionRelationship() {
        if (!extractionRelationshipToAddId) {
            return;
        }

        setJobForm(current => ({
            ...current,
            extractionArtifactIds: current.extractionArtifactIds.includes(extractionRelationshipToAddId)
                ? current.extractionArtifactIds
                : [...current.extractionArtifactIds, extractionRelationshipToAddId],
        }));
        setSaveSuccess(null);
    }

    function handleRemoveRelationship(artifactId: number) {
        setJobForm(current => ({
            ...current,
            artifactIds: current.artifactIds.filter(id => id !== artifactId),
        }));
        setSaveSuccess(null);
    }

    function handleRemovePluginRelationship(artifactId: number) {
        setJobForm(current => ({
            ...current,
            pluginArtifactIds: current.pluginArtifactIds.filter(id => id !== artifactId),
        }));
        setSaveSuccess(null);
    }

    function handleRemoveExtractionRelationship(artifactId: number) {
        setJobForm(current => ({
            ...current,
            extractionArtifactIds: current.extractionArtifactIds.filter(id => id !== artifactId),
        }));
        setSaveSuccess(null);
    }

    function updateEditorText(nextText: string, selectionStart?: number, selectionEnd?: number) {
        if (section === 'jobs') {
            setJobForm(current => ({ ...current, text: nextText }));
        } else {
            setArtifactForm(current => ({ ...current, text: nextText }));
        }

        setPreviewError(null);
        setPreviewText(null);
        setSaveSuccess(null);

        if (selectionStart !== undefined && selectionEnd !== undefined) {
            window.requestAnimationFrame(() => {
                if (!editorRef.current) {
                    return;
                }

                editorRef.current.focus();
                editorRef.current.setSelectionRange(selectionStart, selectionEnd);
            });
        }
    }

    async function handleSave() {
        if (!serviceName) {
            return;
        }

        setSaving(true);
        setSaveError(null);
        setSaveSuccess(null);

        try {
            if (section === 'jobs') {
                const trimmedMaxConcurrency = jobForm.maxConcurrency.trim();
                let parsedMaxConcurrency: number | null = null;
                if (trimmedMaxConcurrency.length > 0) {
                    parsedMaxConcurrency = Number(trimmedMaxConcurrency);
                    if (!Number.isInteger(parsedMaxConcurrency) || parsedMaxConcurrency <= 0) {
                        throw new Error('Max concurrency must be a positive integer.');
                    }
                }

                const payload = {
                    name: jobForm.name,
                    taskType: jobForm.taskType,
                    text: jobForm.text,
                    disabled: jobForm.disabled,
                    canCreatePR: jobForm.canCreatePR,
                    canPublishPR: jobForm.canPublishPR,
                    agentArtifactId: jobForm.agentArtifactId ?? null,
                    pipelineArtifactId: jobForm.pipelineArtifactId ?? null,
                    dependsOnJobDefinitionId: jobForm.dependsOnJobDefinitionId ?? null,
                    executionBackend: jobForm.executionBackend ?? 0,
                    maxConcurrency: parsedMaxConcurrency,
                    modelConfiguration: normalizeJobModelConfiguration(jobForm.modelConfiguration),
                    artifactIds: jobForm.artifactIds,
                    extractionArtifactIds: jobForm.extractionArtifactIds,
                    pluginArtifactIds: jobForm.pluginArtifactIds,
                };
                const result = await ApiHelper(
                    mode === 'create'
                        ? `/api/service/${serviceName}/jobdefinitions`
                        : `/api/service/${serviceName}/jobdefinitions/${encodeURIComponent(selectedName)}`,
                    mode === 'create' ? 'POST' : 'PATCH',
                    payload,
                ) as JobDefinitionDetailResult;

                setJobForm({
                    name: result.name,
                    taskType: result.taskType,
                    text: result.text,
                    disabled: result.disabled,
                    canCreatePR: result.canCreatePR ?? false,
                    canPublishPR: result.canPublishPR ?? false,
                    agentArtifactId: result.agentArtifactId ?? undefined,
                    pipelineArtifactId: result.pipelineArtifactId ?? undefined,
                    dependsOnJobDefinitionId: result.dependsOnJobDefinitionId ?? undefined,
                    executionBackend: result.executionBackend ?? 0,
                    maxConcurrency: result.maxConcurrency !== null && result.maxConcurrency !== undefined
                        ? String(result.maxConcurrency)
                        : '',
                    effectiveMaxConcurrency: result.effectiveMaxConcurrency ?? getDefaultMaxConcurrencyForBackend(result.executionBackend ?? 0),
                    modelConfiguration: normalizeJobModelConfiguration(result.modelConfiguration),
                    artifactIds: (result.relatedArtifacts ?? [])
                        .filter(artifact => artifact.type === 1 || artifact.type === 2)
                        .map(artifact => artifact.id),
                    extractionArtifactIds: (result.relatedArtifacts ?? [])
                        .filter(artifact => artifact.type === 7)
                        .map(artifact => artifact.id),
                    pluginArtifactIds: (result.relatedArtifacts ?? [])
                        .filter(artifact => artifact.type === 5)
                        .map(artifact => artifact.id),
                });
                await loadCatalog();
                updateRoute({ name: result.name, mode: 'edit' });
                setSaveSuccess(`Saved job definition '${result.name}'.`);
            } else {
                let payload: { name: string; type: number; text: string; disabled: boolean };
                if (section === 'agents') {
                    const populatedInputs = agentForm.inputs.filter(input => input.key.trim().length > 0 || input.value.trim().length > 0);
                    if (!agentForm.runtimeName.trim()) {
                        throw new Error('Runtime name is required.');
                    }

                    const inputKeys = populatedInputs.map(input => input.key.trim().toLowerCase()).filter(Boolean);
                    if (inputKeys.length !== new Set(inputKeys).size) {
                        throw new Error('Agent input keys must be unique.');
                    }

                    if (populatedInputs.some(input => input.value.trim().length > 0 && input.key.trim().length === 0)) {
                        throw new Error('Each agent input requires a key.');
                    }

                    payload = {
                        name: agentForm.name,
                        type: 4,
                        text: serializeAgentArtifactForm({
                            ...agentForm,
                            inputs: populatedInputs,
                        }, pluginDefinitionArtifacts),
                        disabled: agentForm.disabled,
                    };
                } else if (section === 'plugins') {
                    if (pluginForm.resolutionType === 'Spec') {
                        const specValidationError = validatePluginSpecForm(pluginForm);
                        if (specValidationError) {
                            throw new Error(specValidationError);
                        }
                    } else if (!pluginForm.runtimeName.trim()) {
                        throw new Error('Runtime name is required.');
                    }

                    payload = {
                        name: pluginForm.name,
                        type: 5,
                        text: serializePluginArtifactForm(pluginForm),
                        disabled: pluginForm.disabled,
                    };
                } else if (section === 'pipelines') {
                    const parsedPipelineId = Number(pipelineForm.pipelineId);
                    if (!Number.isInteger(parsedPipelineId) || parsedPipelineId <= 0) {
                        throw new Error('PipelineId is required and must be a positive integer.');
                    }

                    payload = {
                        name: pipelineForm.name,
                        type: 6,
                        text: serializePipelineArtifactForm(pipelineForm),
                        disabled: pipelineForm.disabled,
                    };
                } else if (section === 'extractions') {
                    if (extractionForm.mode === 'Regex' && !extractionForm.regexPattern.trim()) {
                        throw new Error('Regex pattern is required when mode is Regex.');
                    }

                    if (extractionForm.mode === 'Delimited' && !extractionForm.startDelimiter.trim()) {
                        throw new Error('Start delimiter is required when mode is Delimited.');
                    }

                    payload = {
                        name: extractionForm.name,
                        type: 7,
                        text: serializeExtractionArtifactForm(extractionForm),
                        disabled: extractionForm.disabled,
                    };
                } else {
                    payload = {
                        name: artifactForm.name,
                        type: artifactForm.type,
                        text: artifactForm.text,
                        disabled: artifactForm.disabled,
                    };
                }
                const result = await ApiHelper(
                    mode === 'create'
                        ? `/api/service/${serviceName}/artifacts`
                        : `/api/service/${serviceName}/artifacts/${encodeURIComponent(selectedName)}`,
                    mode === 'create' ? 'POST' : 'PATCH',
                    payload,
                ) as ArtifactSummary;

                if (section === 'agents') {
                    setAgentForm(mapAgentArtifactToForm(result, pluginDefinitionArtifacts));
                } else if (section === 'plugins') {
                    setPluginForm(mapPluginArtifactToForm(result));
                } else if (section === 'pipelines') {
                    setPipelineForm(mapPipelineArtifactToForm(result));
                } else if (section === 'extractions') {
                    setExtractionForm(mapExtractionArtifactToForm(result));
                } else {
                    setArtifactForm({
                        name: result.name,
                        type: result.type,
                        text: result.text,
                        disabled: result.disabled,
                    });
                }
                await loadCatalog();
                updateRoute({ name: result.name, mode: 'edit' });
                setSaveSuccess(`Saved ${getArtifactTypeLabel(result.type).toLowerCase()} '${result.name}'.`);
            }
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : 'Failed to save.');
        } finally {
            setSaving(false);
        }
    }

    async function handlePreview() {
        if (!serviceName) {
            return;
        }

        setPreviewLoading(true);
        setPreviewError(null);

        try {
            const response = await ApiHelper(`/api/service/${serviceName}/artifacts:preview`, 'POST', {
                text: previewSourceText,
            }) as ArtifactPreviewResult;
            setPreviewText(response.text ?? '');
        } catch (err) {
            setPreviewError(err instanceof Error ? err.message : 'Failed to preview snippet expansion.');
            setPreviewText(null);
        } finally {
            setPreviewLoading(false);
        }
    }

    function handleInsertSnippet(snippetNameOverride?: string) {
        const snippetName = snippetNameOverride ?? selectedSnippetName;
        if (!snippetName) {
            return;
        }

        const currentText = section === 'jobs' ? jobForm.text : artifactForm.text;
        const selectionStart = editorRef.current?.selectionStart ?? currentText.length;
        const selectionEnd = editorRef.current?.selectionEnd ?? currentText.length;
        const next = insertSnippetReference(currentText, selectionStart, selectionEnd, snippetName);
        updateEditorText(next.text, next.selectionStart, next.selectionEnd);
    }

    function handleInsertSubstitution(tokenOverride?: string) {
        const substitutionToken = tokenOverride ?? selectedSubstitutionToken;
        if (!substitutionToken) {
            return;
        }

        const currentText = section === 'jobs' ? jobForm.text : artifactForm.text;
        const selectionStart = editorRef.current?.selectionStart ?? currentText.length;
        const selectionEnd = editorRef.current?.selectionEnd ?? currentText.length;
        const next = insertSubstitutionReference(currentText, selectionStart, selectionEnd, substitutionToken);
        updateEditorText(next.text, next.selectionStart, next.selectionEnd);
    }

    async function handleDuplicate() {
        if (!serviceName || !selectedName || duplicateTargetServiceId === undefined) {
            return;
        }

        setDuplicateSubmitting(true);
        setDuplicateError(null);

        try {
            await ApiHelper(`/api/service/${serviceName}/jobdefinitions/${encodeURIComponent(selectedName)}:duplicate`, 'POST', {
                targetServiceId: duplicateTargetServiceId,
                targetName: duplicateTargetName,
                targetTaskType: duplicateTaskType,
            });

            const targetService = allServices.find(candidate => candidate.id === duplicateTargetServiceId);
            if (!targetService) {
                throw new Error(`Target service '${duplicateTargetServiceId}' was not found in the current configuration.`);
            }

            if (targetService.name === serviceName) {
                await loadCatalog();
                updateRoute({ section: 'jobs', name: duplicateTargetName, mode: 'edit' });
            } else {
                navigate(buildJobDefinitionUrl(targetService.name, duplicateTargetName));
            }

            setDuplicatePanelOpen(false);
        } catch (err) {
            setDuplicateError(err instanceof Error ? err.message : 'Failed to duplicate job definition.');
        } finally {
            setDuplicateSubmitting(false);
        }
    }

    const sectionMetadata = DEFINITION_SECTION_OPTIONS.find(option => option.key === section) ?? DEFINITION_SECTION_OPTIONS[0];
    const serviceOptions = allServices.map(item => ({ key: item.id, text: item.name }));
    const isSnippetSection = section === 'snippets';
    const isJobSection = section === 'jobs';
    const isAgentSection = section === 'agents';
    const isPluginSection = section === 'plugins';
    const isPipelineSection = section === 'pipelines';
    const isExtractionSection = section === 'extractions';
    const pluginSpecPreview = buildSpecEncodedReferencePreview(pluginForm.specFormat, pluginForm.specSegments);
    const pluginSpecWarning = getSpecWarning(pluginForm.specFormat);
    const snippetValidationError = isSnippetSection
        ? getSnippetArtifactValidationError(artifactForm.text)
        : null;

    return (
        <PageShell title={`Job definitions${serviceName ? ` - ${serviceName}` : ''}`}>
            <Stack horizontal horizontalAlign="space-between" verticalAlign="end" wrap tokens={{ childrenGap: 12 }}>
                <Text variant="medium">
                    Manage SQL-backed jobs, Agency v2 agents, plugins, pipelines, extraction rules, skills, scripts, and snippets for the current service.
                </Text>
                <Stack horizontal tokens={{ childrenGap: 8 }}>
                    <DefaultButton text="Back to task manager" onClick={() => navigate(`/service/${serviceName}/agenttasks`)} />
                    <DefaultButton
                        text={catalogLoading ? 'Refreshing...' : 'Refresh'}
                        onClick={() => {
                            void loadCatalog();
                            setDetailReloadToken(token => token + 1);
                        }}
                        disabled={catalogLoading}
                    />
                </Stack>
            </Stack>

            {catalogError && (
                <MessageBar messageBarType={MessageBarType.error}>{catalogError}</MessageBar>
            )}

            <div
                style={{
                    border: '1px solid #e1dfdd',
                    borderRadius: 8,
                    padding: 12,
                    background: '#ffffff',
                }}
            >
                <Stack horizontal wrap tokens={{ childrenGap: 8 }} styles={{ root: { rowGap: 8 } }}>
                    {DEFINITION_SECTION_OPTIONS.map(option => (
                        <DefaultButton
                            key={option.key}
                            text={option.text}
                            primary={option.key === section}
                            onClick={() => updateRoute({ section: option.key, name: null, mode: 'create' })}
                        />
                    ))}
                </Stack>
            </div>

            <Stack horizontal wrap={false} tokens={{ childrenGap: 16 }} styles={{ root: { alignItems: 'flex-start' } }}>
                <DefinitionCatalog
                    sectionMetadata={sectionMetadata}
                    currentSectionItems={currentSectionItems}
                    mode={mode}
                    selectedName={selectedName}
                    catalogLoading={catalogLoading}
                    onSelectItem={name => updateRoute({ name, mode: 'edit' })}
                    onCreate={() => updateRoute({ mode: 'create', name: null })}
                />

                <DefinitionEditor
                    mode={mode}
                    selectedName={selectedName}
                    isJobSection={isJobSection}
                    isSnippetSection={isSnippetSection}
                    isAgentSection={isAgentSection}
                    isPluginSection={isPluginSection}
                    isPipelineSection={isPipelineSection}
                    isExtractionSection={isExtractionSection}
                    hasSnippetPreview={hasSnippetPreview}
                    previewLoading={previewLoading}
                    saving={saving}
                    detailLoading={detailLoading}
                    detailError={detailError}
                    saveError={saveError}
                    saveSuccess={saveSuccess}
                    previewError={previewError}
                    snippetValidationError={snippetValidationError}
                    jobForm={jobForm}
                    artifactForm={artifactForm}
                    agentForm={agentForm}
                    pluginForm={pluginForm}
                    pipelineForm={pipelineForm}
                    extractionForm={extractionForm}
                    relatedArtifacts={relatedArtifacts}
                    extractionArtifacts={selectedExtractionArtifacts}
                    pluginArtifacts={pluginArtifacts}
                    agencyModelOptions={agencyModelOptions}
                    agencyModelsLoading={agencyModelsLoading}
                    agencyModelsMessage={agencyModelsMessage}
                    relationshipOptions={relationshipOptions}
                    extractionRelationshipOptions={extractionRelationshipOptions}
                    pluginRelationshipOptions={pluginRelationshipOptions}
                    bundledPluginOptions={bundledPluginOptions}
                    relationshipToAddId={relationshipToAddId}
                    extractionRelationshipToAddId={extractionRelationshipToAddId}
                    pluginRelationshipToAddId={pluginRelationshipToAddId}
                    selectedAgentOptions={selectedAgentOptions}
                    selectedPipelineOptions={selectedPipelineOptions}
                    dependsOnOptions={dependsOnOptions}
                    enabledSnippets={enabledSnippets}
                    selectedSnippetName={selectedSnippetName}
                    availableSubstitutions={JOB_RUNTIME_SUBSTITUTION_OPTIONS}
                    selectedSubstitutionToken={selectedSubstitutionToken}
                    selectedInsertionKind={selectedInsertionKind}
                    previewSourceText={previewSourceText}
                    previewText={previewText}
                    pluginSpecPreview={pluginSpecPreview}
                    pluginSpecWarning={pluginSpecWarning}
                    editorRef={editorRef}
                    setJobForm={setJobForm}
                    setArtifactForm={setArtifactForm}
                    setAgentForm={setAgentForm}
                    setPluginForm={setPluginForm}
                    setPipelineForm={setPipelineForm}
                    setExtractionForm={setExtractionForm}
                    onPreview={() => void handlePreview()}
                    onOpenDuplicate={openDuplicatePanel}
                    onSave={() => void handleSave()}
                    onRelationshipToAddIdChange={setRelationshipToAddId}
                    onAddRelationship={handleAddRelationship}
                    onRemoveRelationship={handleRemoveRelationship}
                    onExtractionRelationshipToAddIdChange={setExtractionRelationshipToAddId}
                    onAddExtractionRelationship={handleAddExtractionRelationship}
                    onRemoveExtractionRelationship={handleRemoveExtractionRelationship}
                    onPluginRelationshipToAddIdChange={setPluginRelationshipToAddId}
                    onAddPluginRelationship={handleAddPluginRelationship}
                    onRemovePluginRelationship={handleRemovePluginRelationship}
                    onSelectedSnippetNameChange={setSelectedSnippetName}
                    onSelectedSubstitutionTokenChange={setSelectedSubstitutionToken}
                    onSelectedInsertionKindChange={setSelectedInsertionKind}
                    onInsertSnippet={handleInsertSnippet}
                    onInsertSubstitution={handleInsertSubstitution}
                    onUpdateEditorText={updateEditorText}
                />
            </Stack>
            <DuplicateDefinitionPanel
                isOpen={isDuplicatePanelOpen}
                serviceOptions={serviceOptions}
                duplicateTargetServiceId={duplicateTargetServiceId}
                duplicateTargetName={duplicateTargetName}
                duplicateTaskType={duplicateTaskType}
                duplicateSubmitting={duplicateSubmitting}
                duplicateError={duplicateError}
                onDismiss={() => setDuplicatePanelOpen(false)}
                onDuplicate={() => void handleDuplicate()}
                onTargetServiceIdChange={setDuplicateTargetServiceId}
                onTargetNameChange={setDuplicateTargetName}
                onTargetTaskTypeChange={setDuplicateTaskType}
            />
        </PageShell>
    );
}
